import { Trans, useTranslation } from 'react-i18next';
import { Alert, Button, Dialog, DialogContent, OtpInput, Text } from '@datanomik/core-ui';
import { ShieldCheckLine } from '@datanomik/core-ui/icons';

import { useAccountVerification } from '../hooks/useAccountVerification';
import type { Session } from '../model/types';

interface AccountVerificationDialogProps {
  session: Session;
  onVerified: () => void;
  onCancel: () => void;
}

export function AccountVerificationDialog({
  session,
  onVerified,
  onCancel,
}: AccountVerificationDialogProps) {
  const { t } = useTranslation();
  const {
    code, setCode, busy, error, notice, complete, codeLength, confirm, resend,
  } = useAccountVerification(session.userId);

  const email = session.email ?? t('email');

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent
        icon={<ShieldCheckLine />}
        title={t('Verification')}
        description={(
          <Trans
            i18nKey="Enter the verification code that has been sent to {{email}}"
            values={{ email }}
            components={{ strong: <strong className="font-medium text-foreground" /> }}
          />
        )}
        footer={(
          <>
            <Button
              className="w-full"
              loading={busy}
              disabled={!complete}
              onClick={async () => { if (await confirm()) onVerified(); }}
            >
              {t('Verify Email')}
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" className="flex-1" onClick={() => resend('EMAIL')}>
                {t('Resend email verification code')}
              </Button>
              <Button variant="ghost" className="flex-1" onClick={() => resend('SMS')}>
                {t('Resend mobile verification code')}
              </Button>
            </div>
          </>
        )}
      >
        <div className="flex flex-col gap-3">
          {error && <Alert tone="destructive">{error}</Alert>}
          {notice && <Alert tone="success">{notice}</Alert>}

          <OtpInput
            value={code}
            onChange={setCode}
            length={codeLength}
            invalid={Boolean(error)}
            autoFocus
            label={t('Verification code')}
            onComplete={async () => { if (!busy && await confirm()) onVerified(); }}
          />

          <Text variant="caption" tone="muted" className="text-center">
            {t('This code is valid for 60 minutes. Some Outlook users may experience delays in receiving the confirmation email. Please check your SPAM inbox.')}
          </Text>
        </div>
      </DialogContent>
    </Dialog>
  );
}
